// ============= Imports =============

import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

export interface CancelWorkflowParams {
  id: number;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

export interface CancelWorkflowResponse {
  success: boolean;
  data?: unknown;
  error?: string;
  status?: number;
}

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Workflow';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const HEADERS = { 'Content-Type': 'application/json' };

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

// ============= Main Function =============
/**
 * Cancels a workflow
 * @param params Parameters for workflow cancellation
 * @returns Promise with API response containing cancellation result or error
 */
export const cancelWorkflow = async ({
  id,
  getAccessToken,
  spoofedUser,
}: CancelWorkflowParams): Promise<CancelWorkflowResponse> => {
  try {
    console.log('🔄 Canceling workflow...');

    // Token acquisition with required scopes
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      return {
        success: false,
        error: 'Failed to acquire API token',
      };
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${id}/cancel`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request - cancellation doesn't need a body
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token, spoofedUser || null),
    });

    // Response handling
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;

      // Try to get more detailed error information from response
      try {
        const contentType = response.headers.get('content-type') || '';
        let errorData: unknown = null;
        if (contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          // Handle plain text error bodies
          errorData = await response.text();
        }

        if (typeof errorData === 'string') {
          const trimmed = errorData.trim();
          if (trimmed) {
            errorMessage = `${errorMessage}: ${trimmed}`;
          }
        } else if (errorData && typeof errorData === 'object') {
          const asRecord = errorData as Record<string, unknown>;
          if (typeof asRecord.message === 'string' && asRecord.message) {
            errorMessage = `${errorMessage}: ${asRecord.message}`;
          }
        }
      } catch {
        // If we can't parse the error response, use the status text
        errorMessage = `${errorMessage}: ${
          response.statusText || 'Unknown error'
        }`;
      }

      console.log('❌ Workflow cancellation failed:', errorMessage);
      return {
        success: false,
        error: errorMessage,
        status: response.status,
      };
    }

    // Parse successful response
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      console.warn('⚠️ Response parsing failed, treating as empty response');
      data = null;
    }

    console.log('✅ Workflow canceled successfully');
    return {
      success: true,
      data,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'An unknown error occurred';
    console.error('🔴 Error canceling workflow:', errorMessage);
    return {
      success: false,
      error: errorMessage,
    };
  }
};
